import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {withSize} from 'react-sizeme';
import useSWR from 'swr';
import axiosInstance from '../../shared/utils/axios';
import debug from 'debug';
import {formatter} from '../../shared/utils/utils';
import useQueryObject from '../../shared/utils/useQueryObject';
import history from '../../history';
import {getBackPath} from '../shared/utils/dashboardUtils';
import {
  AccountNumberMask,
  Column,
  Columnizer,
  DescriptiveText,
  Icon,
  LoadingSpinner,
  Page,
  Row,
  Table,
  TextInput,
  Tile,
  TitleLabel,
} from '../../shared';

const log = debug('circleblack:investor:page:AccountInfoPage');

const AccountInfoPage = ({size}) => {
  log('rendering');
  const [context] = useQueryObject('context');

  const backPath = getBackPath();

  const {data: account, error: accountError} = useSWR(
    context.containerID && `/containers/${context.containerID}/accounts`,
    url => axiosInstance.get(url).then(res => res.data[0])
  );

  const {data: positionsCount, error: positionsCountError} = useSWR(
    context.containerID && `containers/${context.containerID}/positions/count`
  );

  const [error, setError] = useState();
  log('accountInfoPage');

  useEffect(() => {
    if (accountError?.response?.data || positionsCountError?.response?.data) {
      setError(accountError?.response?.data.error || positionsCountError?.response?.data.error);
    }
  }, [accountError, positionsCountError]);

  if (!account) {
    if (error) {
      return <>{error.msg || 'Failed to load data from API.'}</>;
    }
    return <LoadingSpinner/>;
  }

  const gatherAdvisorsData = () => (inputValue, config) => account && axiosInstance.get(`/accounts/${account.id}/advisors`, {params: {...inputValue}, ...config})
    .then(res => res.data)
    .catch(err => {
      log('error', err);
      throw err;
    });

  const gatherAdvisors = gatherAdvisorsData();

  const dataManager = {
    gatherData: inputValue => gatherAdvisors({
      ...inputValue,
    }),
  };

  const columns = [
    {
      title: 'Managed by',
      dataPoint: 'displayName',
      type: 'text',
      minWidth: 175,
      isSortable: true,
    },
    (size.width >= 390) && {
      title: 'Email',
      dataPoint: 'email',
      type: 'text',
      minWidth: 215,
      isSortable: true,
    },
    (size.width >= 500) && {
      title: 'Phone',
      dataPoint: 'phone',
      type: 'phone',
      minWidth: 110,
      isSortable: true,
    },
    (size.width >= 605) && {
      title: 'Managed since',
      dataPoint: 'managedSince',
      alignment: 'right',
      type: 'date',
      minWidth: 105,
      isSortable: true,
    },
  ].filter(c => !!c);

  /* TODO change when the KST-2901 is ready */
  return <Page>
    <Tile key={'accountInfo'}
          title={'Account Info'}
          icon={<Icon svg={'arrowCircle'}
                      rotate={'180deg'}
                      action={() => history.push(backPath)}/>}>
      <Row justifySubRow={'left'} width={'100%'} margin={'20px 10px 0 10px'} wrap={true}>
        <Columnizer alignItems='top' height='auto' justifySubRow={'left'} itemWidth={size.width >= 920 ? 300 : 140} itemGrow={true} maxWidth={'100%'} wrap={true}>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TextInput type={'text'}
                       title={'Account Name'}
                       placeholder={''}
                       dataPoint={{
                         name: 'accountName',
                         value: account.name,
                       }}
                       readOnly={true}
                       margin={'0'}/>
          </Column>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TitleLabel title={'Account #'}/>
            <DescriptiveText alignment={'left'} fontSize={'12px'} margin={'10px 0'}>
              <AccountNumberMask number={account.number}
                                 institution={account.institution}
                                 institutionName={account.institution.name}/>
            </DescriptiveText>
          </Column>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TextInput type={'text'}
                       title={'Account Type'}
                       placeholder={''}
                       dataPoint={{name: 'clients', value: account.type}}
                       readOnly={true}
                       margin={'0'}/>
          </Column>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TextInput type={'text'}
                       title={'# of Holdings'}
                       placeholder={''}
                       dataPoint={{name: 'holdings', value: positionsCount}}
                       readOnly={true}
                       margin={'0'}/>
          </Column>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TextInput type={'text'}
                       title={'Total Value'}
                       placeholder={''}
                       dataPoint={{name: 'accountBalance', value: formatter('currency')(account.balance)}}
                       readOnly={true}
                       margin={'0'}/>
          </Column>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TextInput type={'text'}
                       title={'Institution'}
                       placeholder={''}
                       dataPoint={{name: 'institutionName', value: account.institution?.name}}
                       readOnly={true}
                       margin={'0'}/>
          </Column>
          <Column alignItems={'left'} width={'100%'} margin={'20px 0 20px 0'}>
            <TextInput type={'text'}
                       title={'As Of'}
                       placeholder={''}
                       dataPoint={{name: 'asOfDate', value: moment(account.asOfDate).format('M/D/YY')}}
                       readOnly={true}
                       margin={'0'}/>
          </Column>
        </Columnizer>
      </Row>
      <Table dataManager={dataManager}
             columns={columns}
             defaultSort={{
               column: 'displayName',
               desc: true,
             }}/>
    </Tile>
  </Page>;
};

export default withSize()(AccountInfoPage);

AccountInfoPage.propTypes = {
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
};
